import React, { useState } from 'react';
import Modal from '../common/Modal';
import ConfirmDialog from '../common/ConfirmDialog';
import EventDetailView from './EventDetailView';
import EventForm from './EventForm';
import { useDeleteEvent } from '../../hooks/useCalendarEvents';
import type { CalendarEvent } from '../../types/integrations';

export type EventModalMode = 'create' | 'edit' | 'view';

interface EventModalProps {
  mode: EventModalMode;
  event?: CalendarEvent;
  initialDate?: Date;
  onClose: () => void;
}

const EventModal: React.FC<EventModalProps> = ({
  mode: initialMode,
  event,
  initialDate,
  onClose,
}) => {
  const [mode, setMode] = useState<EventModalMode>(initialMode);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const deleteMutation = useDeleteEvent();

  // create + edit live inside EventForm (which renders its own Modal).
  if ((mode === 'create' || mode === 'edit') && (mode === 'create' || event)) {
    return (
      <EventForm
        mode={mode}
        event={event}
        initialDate={initialDate}
        onClose={onClose}
      />
    );
  }

  // view mode owns its own Modal shell.
  if (!event) {
    onClose();
    return null;
  }

  const handleDelete = async () => {
    setConfirmDelete(false);
    try {
      await deleteMutation.mutateAsync({
        eventId: event.id,
        calendarId: event.calendar_id,
      });
      onClose();
    } catch {
      /* hook surfaces toast */
    }
  };

  return (
    <>
      <Modal isOpen onClose={onClose} title="" size="2xl">
        <EventDetailView
          event={event}
          onEdit={() => setMode('edit')}
          onDelete={() => setConfirmDelete(true)}
        />
      </Modal>
      {confirmDelete && (
        <ConfirmDialog
          title="Delete This Event?"
          message="This will remove the event from your Tereiko calendar and notify attendees."
          confirmLabel={deleteMutation.isPending ? 'Deleting…' : 'Delete'}
          confirmVariant="danger"
          onConfirm={handleDelete}
          onCancel={() => setConfirmDelete(false)}
        />
      )}
    </>
  );
};

export default EventModal;
